import { useEffect, useState, useMemo } from 'react';

import debounce from 'debounce-promise';

import { DEFAULT_DEBOUNCE_TIMEOUT } from 'utils/constants';
import { GeoAttrs, getAddressParts } from 'utils/address';

export enum Status {
  Ok = 'Ok',
  Error = 'Error',
  NotReady = 'NotReady',
}

export function useGooglePlace() {
  const [predictions, setPredictions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const [status, setStatus] = useState<Status>(Status.NotReady);
  const [placesLibrary, setPlacesLibrary] = useState<google.maps.PlacesLibrary>();

  useEffect(() => {
    async function loadLibrary() {
      try {
        const library = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;

        setPlacesLibrary(library);
        setStatus(Status.Ok);
      } catch (error) {
        console.error('Failed to load Google Places library', error);
        setStatus(Status.Error);
      }
    }

    if (!!window.google?.maps?.importLibrary) {
      loadLibrary();
    } else {
      setStatus(Status.Error);
    }
  }, [!!window.google?.maps?.importLibrary]);

  const getSuggestions = useMemo(() => {
    if (!placesLibrary) {
      return async () => [];
    }

    const fetchSuggestions = async (input: string) => {
      if (!input) {
        setPredictions([]);

        return [];
      }

      try {
        const result = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: input,
          sessionToken: new placesLibrary.AutocompleteSessionToken(),
          includedRegionCodes: ['us'],
          language: 'en',
        });

        setPredictions(result.suggestions || []);
        setStatus(Status.Ok);

        return result.suggestions;
      } catch (error: any) {
        setStatus(Status.Error);

        return [];
      }
    };

    return debounce(fetchSuggestions, DEFAULT_DEBOUNCE_TIMEOUT);
  }, [placesLibrary]);

  const getPlaceDetails = async (placeId: string): Promise<GeoAttrs> => {
    if (!placesLibrary) {
      return {
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
        county: '',
      };
    }

    const place = new placesLibrary.Place({
      id: placeId,
      requestedLanguage: 'en',
    });
    const details = await place.fetchFields({
      fields: ['addressComponents', 'formattedAddress'],
    });

    if (details.place?.addressComponents) {
      const parts = getAddressParts(details.place.addressComponents);

      return parts;
    } else {
      return {
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
        county: '',
      };
    }
  };

  const clearSuggestions = () => {
    setPredictions([]);
  };

  return {
    predictions: predictions,
    getSuggestions: getSuggestions,
    status: status,
    clearSuggestions: clearSuggestions,
    getPlaceDetails: getPlaceDetails,
  };
}
